import { RestartAltRounded, SwapHorizRounded } from '@mui/icons-material';
import { Box, Button, ButtonBase, MenuItem, Popover, Tooltip, Typography } from '@mui/material';
import { formatInTimeZone } from 'date-fns-tz';
import { FC, MouseEvent, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { resolveLocationBadge } from 'src/components/dispatch/detailPanel/locationBadge';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { quantityValueText } from 'src/components/dispatch/metricText';
import { DispatchCard } from 'src/entities/dispatch';

import { LongHaulMoveTarget } from './buildLongHaulMoveTargets';
import { tripCardsOf } from './resolveDirectStopCard';
import { LongHaulTrip, TripSpan } from './types';

type Props = {
  trip: LongHaulTrip;
  /** 「他レーンへ移す」の候補。buildLongHaulMoveTargets の結果をそのまま渡す */
  moveTargets: readonly LongHaulMoveTarget[];
  /** 押された地点のカード。呼び出し側が DispatchSelection に載せ替える */
  onSelectStop: (card: DispatchCard) => void;
  /** バーが含む全 legRun。運行まるごと未割当へ戻す */
  onUnassign: (legRunIds: number[]) => void;
  onMoveTo: (target: { shiftTruckId: number; targetRowId: string }) => void;
};

const BUSINESS_TIME_ZONE = 'Asia/Tokyo';

/**
 * ISO → 業務日（JST）の M/d。不正な日時は null。
 *
 * Why not date-fns の format: timeZone を無視するため実行環境のタイムゾーンで出てしまう
 * （FE 既存バグ #2376）。実際に JST へ変換する formatInTimeZone を使う。
 */
const jstDayOf = (iso: string): string | null => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return formatInTimeZone(date, BUSINESS_TIME_ZONE, 'M/d');
};

/** 運行期間。積地の到着日 → 降地の到着日（バーと同じ表示区間） */
const periodTextOf = (span: TripSpan): string => {
  const start = jstDayOf(span.displayStartAt);
  const end = jstDayOf(span.displayEndAt);
  if (start == null) return end ?? '';
  if (end == null) return start;
  return `${start} → ${end}`;
};

/** 地点名。DispatchDetailPanel の表題 (DispatchDetailPanel.tsx:503-507) と同じ決め方 */
const stopNameOf = (card: DispatchCard): string => (
  card.type === 'loading' ? card.locationName ?? '積み込み拠点' : card.storeName
);

const labelSx = { fontSize: 11, color: 'hsl(var(--muted-foreground))' } as const;

/**
 * 運行単位の詳細パネル。DetailPanelShell の children モードに差し込む。
 *
 * 運行全体に効く操作（未割当へ戻す・他レーンへ移す）はここに置き、地点を押したときは
 * カードを返すだけにする。適用先を「移動・未割当化は運行全体」「座標編集は選んだ 1 地点」
 * で分けるため（地点側は既存の DispatchDetailPanel が受け持つ）。
 */
const LongHaulTripDetail: FC<Props> = ({
  trip, moveTargets, onSelectStop, onUnassign, onMoveTo,
}) => {
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);
  const canMove = moveTargets.length > 0;

  /** 操作計測。粒度は V3Plans に合わせる */
  const track = (buttonName: string, label?: string | number) => {
    ReactGA.event('click', {
      screen_name: SCREEN_NAMES.PLANNING_LONG_HAUL,
      button_name: buttonName,
      ...(label === undefined ? {} : { label }),
    });
  };

  // 候補が空になってもボタンの DOM は anchor に残る。切り離された DOM を anchor にした
  // Popover が勝手に開くのを防ぐ（StopBulkMoveBar.tsx:41-45 と同じ）
  useEffect(() => {
    if (!canMove) setMoveMenuAnchor(null);
  }, [canMove]);

  const handleOpenMoveMenu = (e: MouseEvent<HTMLElement>) => {
    track('運行パネル 他レーンへ移す', moveTargets.length);
    setMoveMenuAnchor(e.currentTarget);
  };

  const handleSelectTarget = (target: LongHaulMoveTarget) => {
    track('運行パネル 他レーンへ移す 確定');
    setMoveMenuAnchor(null);
    // コマンドに要るのは投入先の勤務とその行だけ。label / subLabel は表示専用なので渡さない
    onMoveTo({ shiftTruckId: target.shiftTruckId, targetRowId: target.targetRowId });
  };

  return (
    <Box data-testid="long-haul-trip-detail">
      <Box sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4 }}>{trip.shipperName}</Box>
      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ fontSize: 13, fontWeight: 500 }}>{periodTextOf(trip.span)}</Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Box component="span" sx={labelSx}>数量</Box>
          <Box component="span" sx={{ fontSize: 13, fontWeight: 500 }}>
            {`${quantityValueText(trip.totalItemCount)} cs`}
          </Box>
        </Box>
      </Box>

      <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 1.5, p: 0 }}>
        {/* バーは積地ごとなので、回転のカード全部ではなくこのバーのぶんだけ並べる */}
        {tripCardsOf(trip).map((card) => {
          const badge = resolveLocationBadge({
            source: 'vehicle',
            cardType: card.type === 'delivery' ? 'delivery' : 'loading',
          });
          const name = stopNameOf(card);
          return (
            <Box component="li" key={card.id} sx={{ mb: 0.5 }}>
              <Tooltip title={badge.label} arrow placement="left">
                <ButtonBase
                  data-testid="long-haul-stop"
                  aria-label={`${badge.label} ${name}`}
                  onClick={() => {
                    track('運行パネル 地点 選択', badge.label);
                    onSelectStop(card);
                  }}
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    px: 1,
                    py: 0.75,
                    fontSize: 13,
                    textAlign: 'left',
                    borderRadius: '8px',
                    border: '1px solid hsl(var(--border))',
                    // 積地・降地は色だけで分ける。バッジの文字を入れると地点名が読みづらくなる
                    borderLeft: `3px solid ${badge.color}`,
                    '&:hover': { bgcolor: 'hsl(var(--muted) / 0.5)' },
                  }}
                >
                  {name}
                </ButtonBase>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RestartAltRounded sx={{ fontSize: 16 }} />}
          onClick={() => {
            track('運行パネル 未割当に戻す', trip.legRunIds.length);
            onUnassign(trip.legRunIds);
          }}
          sx={{ height: 28, fontSize: 12, textTransform: 'none' }}
        >
          未割当に戻す
        </Button>
        {/* 候補が空のまま描くと、押しても見出しだけの空 Popover が開く
            （buildLongHaulMoveTargets.ts / DetailPanelShell と同じ規則） */}
        {canMove && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SwapHorizRounded sx={{ fontSize: 16 }} />}
              onClick={handleOpenMoveMenu}
              sx={{ height: 28, fontSize: 12, textTransform: 'none' }}
            >
              他レーンへ移す
            </Button>
            <Popover
              open={Boolean(moveMenuAnchor)}
              anchorEl={moveMenuAnchor}
              onClose={() => setMoveMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: { width: 240, maxHeight: 320, overflowY: 'auto', p: 0.5 } } }}
            >
              <Typography variant="caption" sx={{ px: 1, py: 0.75, display: 'block', color: 'text.secondary' }}>
                移動先の勤務を選択
              </Typography>
              {moveTargets.map((target) => (
                <MenuItem
                  key={`${target.shiftTruckId}-${target.targetRowId}`}
                  onClick={() => handleSelectTarget(target)}
                  sx={{ fontSize: 13 }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {target.label}
                    </Box>
                    <Box sx={{ fontSize: 11, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {target.subLabel}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
};

export default LongHaulTripDetail;
